/**
 * The words the priority graph prints for its kinds and roles (#15196).
 *
 * One helper, so no surface spells "Outcome" or "Captain" as a literal: the
 * defaults are the system's own words, and an organization's overrides —
 * the re-keyed terminology of #15033 — arrive as {@link
 * PriorityTermOverrides} without any caller changing.
 *
 * #15033 turns the overrides on. A word is keyed by kind, optionally
 * narrowed by horizon altitude (`outcome·quarter` → "Rock"), and the most
 * specific match wins: kind plus horizon, then kind, then the default.
 *
 * Client-safe and pure.
 */

import type { HorizonPrecision } from '@/lib/priority-horizon'
import {
  isPriorityKind,
  PRIORITY_KINDS,
  type PriorityKind,
} from '@/lib/priority-kinds'

/** The assignment roles the map names (`PRIORITY_ASSIGNMENT_ROLES`). */
export const PRIORITY_ROLE_TERMS = [
  'captain',
  'contributor',
  'observer',
] as const
export type PriorityRoleTerm = (typeof PRIORITY_ROLE_TERMS)[number]

/**
 * The everyday word for a responsibility as a group of people: the map's
 * bands, the "No team" bucket and the Scope options. Kept as its own key so
 * an organization can rename it apart from the kind.
 */
export const PRIORITY_GROUP_TERM = 'team' as const

export type PriorityTermKey =
  | PriorityKind
  | PriorityRoleTerm
  | typeof PRIORITY_GROUP_TERM

export interface PriorityTerm {
  singular: string
  plural: string
}

/** The horizon altitudes a word can be narrowed by, widest first. */
export const PRIORITY_TERM_HORIZONS = [
  'year',
  'quarter',
  'month',
  'week',
  'day',
] as const satisfies readonly HorizonPrecision[]

/** Joins a kind and an altitude: `outcome·quarter`. */
export const PRIORITY_HORIZON_TERM_SEPARATOR = '·'

/** A kind narrowed by a horizon altitude, e.g. `outcome·quarter`. */
export type PriorityHorizonTermKey = `${PriorityKind}·${HorizonPrecision}`

export function horizonTermKey(
  kind: PriorityKind,
  horizon: HorizonPrecision,
): PriorityHorizonTermKey {
  return `${kind}${PRIORITY_HORIZON_TERM_SEPARATOR}${horizon}`
}

/** The kind and altitude of a narrowed key; null for anything else. */
export function parseHorizonTermKey(
  key: string,
): { kind: PriorityKind; horizon: HorizonPrecision } | null {
  const [kind, horizon, ...rest] = key.split(PRIORITY_HORIZON_TERM_SEPARATOR)
  if (rest.length > 0 || !isPriorityKind(kind)) return null
  if (!(PRIORITY_TERM_HORIZONS as readonly string[]).includes(horizon)) {
    return null
  }
  return { kind, horizon: horizon as HorizonPrecision }
}

/** The system's own words, lower case. */
export const DEFAULT_PRIORITY_TERMS: Readonly<
  Record<PriorityTermKey, PriorityTerm>
> = Object.freeze({
  anchor: { singular: 'anchor', plural: 'anchors' },
  value: { singular: 'value', plural: 'values' },
  principle: { singular: 'principle', plural: 'principles' },
  responsibility: { singular: 'responsibility', plural: 'responsibilities' },
  practice: { singular: 'practice', plural: 'practices' },
  measure: { singular: 'measure', plural: 'measures' },
  outcome: { singular: 'outcome', plural: 'outcomes' },
  task: { singular: 'task', plural: 'tasks' },
  captain: { singular: 'captain', plural: 'captains' },
  contributor: { singular: 'contributor', plural: 'contributors' },
  observer: { singular: 'observer', plural: 'observers' },
  team: { singular: 'team', plural: 'teams' },
})

/** One override: either form may be unset; an acronym is never lowered. */
export interface PriorityTermOverride extends Partial<PriorityTerm> {
  isAcronym?: boolean
}

/**
 * An organization's words, per key and per form; a blank form is unset. A
 * narrowed key (`outcome·quarter`) wins over its kind for that altitude.
 */
export type PriorityTermOverrides = Partial<
  Record<PriorityTermKey | PriorityHorizonTermKey, PriorityTermOverride>
>

export interface PriorityTermOptions {
  plural?: boolean
  /** First letter upper-cased, for a heading or a label. */
  capitalized?: boolean
  /**
   * The row's horizon altitude: a word for the kind at that altitude wins
   * over the kind's own. Only meaningful for a kind key.
   */
  horizon?: HorizonPrecision | null
}

export interface PriorityTerms {
  term: (key: PriorityTermKey, options?: PriorityTermOptions) => string
}

function capitalize(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1)
}

/**
 * An override's word as a sentence reads it: lower case, unless the org
 * marked it an acronym ("AOR").
 */
function sentenceCase(word: string, override: PriorityTermOverride): string {
  return override.isAcronym ? word : word.toLowerCase()
}

export function priorityTerms(
  overrides: PriorityTermOverrides = {},
): PriorityTerms {
  return {
    term(key, { plural = false, capitalized = false, horizon = null } = {}) {
      const form = plural ? 'plural' : 'singular'
      // Most specific first: kind plus horizon, then kind, then default.
      const candidates: PriorityTermOverride[] = []
      if (horizon !== null && isPriorityKind(key)) {
        const narrowed = overrides[horizonTermKey(key, horizon)]
        if (narrowed) candidates.push(narrowed)
      }
      const own = overrides[key]
      if (own) candidates.push(own)
      for (const override of candidates) {
        const word = override[form]?.trim()
        if (word) {
          return capitalized ? capitalize(word) : sentenceCase(word, override)
        }
      }
      const word = DEFAULT_PRIORITY_TERMS[key][form]
      return capitalized ? capitalize(word) : word
    },
  }
}

/** Each kind's display name, capitalized, in the default words. */
export const DEFAULT_PRIORITY_KIND_LABELS = Object.freeze(
  Object.fromEntries(
    PRIORITY_KINDS.map(kind => [
      kind,
      priorityTerms().term(kind, { capitalized: true }),
    ]),
  ) as Record<PriorityKind, string>,
)
